import React, { useState, useCallback } from "react";
import moment from "moment";
import SubHeading from "../../../../atoms/SubHeading";
import useMeetingRealtime from "../../../../../hooks/useMeetingsRealtime";
import DrawingCanvas from "../../../Reuniones/DrawingCanvas";

const ModalViewOnlineMeeting = ({ meetingId, nameEnterprice }) => {
	const [reunion, setReunion] = useState({
		subject: "-",
		location: "-",
		body: "",
		office_tasks: "",
		client_tasks: "",
		members: { rs: [], ext: [] },
		canvasIncluded: false,
		canvasLines: [],
		createdAt: "",
	});

	// --- Actualiza campos al recibir eventos desde socket ---
	const setFieldValues = useCallback((field, value) => {
		setReunion((prev) => {
			if (field === "membersRS") {
				return { ...prev, members: { ...prev.members, rs: value } };
			}
			if (field === "membersEXT") {
				return { ...prev, members: { ...prev.members, ext: value } };
			}
			if (field === "canvasIncluded") {
				return { ...prev, canvasIncluded: value };
			}
			if (field === "canvasLines") {
				return { ...prev, canvasLines: value };
			}
			return { ...prev, [field]: value };
		});
	}, []);

	useMeetingRealtime({ meetingId, onFieldUpdate: setFieldValues });

	// --- Helpers para renderizar elementos ---
	const renderInfoRow = (label, value) => (
		<div className="flex flex-row space-x-2 items-center">
			<SubHeading text={`${label}:`} color="blue" />
			<SubHeading text={value || "-"} />
		</div>
	);

	const renderTextBlock = (title, content) => {
		if (!content || (Array.isArray(content) && content.length === 0)) {
			return (
				<article className="w-full p-3">
					<SubHeading text={title} color="blue" />
					<p className="whitespace-pre-wrap bg-gray-50 p-3 rounded-md border text-gray-700">
						-
					</p>
				</article>
			);
		}

		if (Array.isArray(content)) {
			return (
				<article className="w-full p-3">
					<SubHeading text={title} color="blue" />
					<ul className="list-decimal list-inside space-y-1 bg-gray-50 p-3 rounded-md border text-gray-700 break-words">
						{content.map((item, index) => (
							<li key={index} className="break-words">
								{item}
							</li>
						))}
					</ul>
				</article>
			);
		}

		return (
			<article className="w-full p-3">
				<SubHeading text={title} color="blue" />
				<p className="whitespace-pre-wrap bg-gray-50 p-3 rounded-md border text-gray-700">
					{content}
				</p>
			</article>
		);
	};

	const renderMemberList = (title, members) => {
		if (!members?.length) return null;
		return (
			<div className="w-full">
				<SubHeading text={title} color="blue" />
				<ul className="list-disc ml-6 space-y-1">
					{members.map((m, index) => {
						// Genera una key única
						const key =
							typeof m.id === "string" || typeof m.id === "number"
								? m.id
								: typeof m.value === "string" || typeof m.value === "number"
								? m.value
								: `${m.name || ""}-${m.email || ""}-${index}`;

						return (
							<li key={key}>
								<strong>Nombre:</strong> {m.name || m.label || "-"}
								{m.email && <span> - {m.email}</span>}
							</li>
						);
					})}
				</ul>
			</div>
		);
	};

	return (
		<section className="w-full space-y-6">
			{/* --- Encabezado --- */}
			<div className="flex flex-col md:flex-row flex-wrap gap-4">
				{renderInfoRow("Asunto", reunion.subject)}
				{renderInfoRow("Lugar", reunion.location)}
				{renderInfoRow(
					"Fecha y hora",
					reunion?.createdAt
						? moment(reunion.createdAt).format("DD/MM/YYYY h:mm a")
						: "-"
				)}
				{renderInfoRow("Empresa", nameEnterprice)}
			</div>

			{/* --- Miembros --- */}
			{(reunion?.members?.rs?.length > 0 ||
				reunion?.members?.ext?.length > 0) && (
				<div className="flex flex-col md:flex-row space-y-6 md:space-y-0 md:space-x-6">
					{renderMemberList("Miembros Internos", reunion.members.rs)}
					{renderMemberList("Miembros Externos", reunion.members.ext)}
				</div>
			)}

			{/* --- Cuerpo de la reunión --- */}
			{renderTextBlock("Reunión", reunion.body)}

			{/* --- Tareas --- */}
			<div className="flex flex-col md:flex-row w-full gap-4">
				{renderTextBlock("Tareas cliente", reunion.client_tasks)}
				{renderTextBlock("Tareas despacho", reunion.office_tasks)}
			</div>

			{reunion.canvasIncluded && (
				<article className="w-full p-3">
					<SubHeading text="Adjuntar nota (Canvas)" color="blue" />
					<div className="border-2 border-gray-300 rounded-lg p-2 bg-gray-50">
						<DrawingCanvas
							readonly
							canvasRef={null}
							remoteLines={reunion.canvasLines}
							meetingId={meetingId}
							height={600}
						/>
					</div>
				</article>
			)}
		</section>
	);
};

export default ModalViewOnlineMeeting;
